import { TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/Language/LanguageContext";
import { fetchMatchViews } from "../utils/dbutils";
import { saveToPC } from "../utils/Utils";

const kNotifyByEmailStorageKey = "VBLiveShareNotifyByEmail";

function Share({ shareStatus, shareUsers, currentTime, onShare, matchId }) {
  const { t } = useLanguage();
  const [thisShareStatus, setThisShareStatus] = useState(0);
  const [originalUsers, setOriginalUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [, forceUpdate] = useState(0);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [viewLog, setViewLog] = useState([]);
  const [notifyByEmail, setNotifyByEmail] = useState(
    () => localStorage.getItem(kNotifyByEmailStorageKey) === "true"
  );
  const usersListRef = useRef();

  // Remembers the user's last choice so it doesn't reset to unchecked every
  // time the Share dialog is reopened.
  const changeNotifyByEmail = (value) => {
    setNotifyByEmail(value);
    localStorage.setItem(kNotifyByEmailStorageKey, String(value));
  };

  const doAddUser = () => {
    const email = document.getElementById("email").value;
    if (email !== "") {
      if (selectedUsers.includes(email)) {
        toast.error(t("User already added"));
        return;
      } else {
        setSelectedUsers([...selectedUsers, email]);
        document.getElementById("email").value = "";
      }
    } else {
      toast.error(t("Please enter a valid email address"));
    }
  };

  const doDeleteUser = (user) => {
    setSelectedUsers(selectedUsers.filter((u) => u !== user));
  };

  const doClearAllUsers = () => {
    setShowClearModal(true);
  };

  const doSaveShare = () => {
    var s = "";
    // Removing every user from the "selected users" list while staying on
    // "Available to selected users" is equivalent to fully revoking the
    // share (there's nobody left it's shared with) — save it as "Not
    // Shared" instead of leaving a meaningless status=2/empty-list
    // combination, so users have a real way to remove ALL of a match's
    // shares (not just pare the list down to one remaining user).
    const statusToSave =
      thisShareStatus === 2 && selectedUsers.length === 0 ? 0 : thisShareStatus;
    if (statusToSave === 2) {
      for (var user of selectedUsers) {
        if (s.length > 0) s += ",";
        s += user;
      }
    }
    onShare({
      shareStatus: statusToSave,
      shareUsers: s,
      notifyByEmail: statusToSave === 2 ? notifyByEmail : false,
    });
  };

  const doShare = (share) => {
    if (share) {
      console.log("Sharing with users: ", selectedUsers);
      doSaveShare();
    } else {
      if (shareStatus !== null && shareStatus !== undefined) {
        if (shareStatus !== thisShareStatus || originalUsers !== selectedUsers) {
          setShowSaveConfirmModal(true);
        } else {
          onShare(null);
        }
      } else {
        onShare(null);
      }
    }
  };

  const doConfirmSave = (save) => {
    setShowSaveConfirmModal(false);
    if (save) {
      doSaveShare();
    } else {
      onShare(null);
    }
  };

  const doDoSaveList = () => {
    const filename = document.getElementById("exportfilename").value;
    if (filename.length === 0) {
      toast.error("Please enter a valid filename");
      return;
    }
    var users = selectedUsers.join("\n");
    var blob = new Blob([users], { type: "text/plain" });
    saveToPC(blob, filename + ".txt");
  };

  const doSaveList = () => {
    setShowFilenameModal(true);
  };

  const handleUsersList = (event) => {
    var url = URL.createObjectURL(event.target.files[0]);
    const fileReader = new FileReader();
    fileReader.readAsText(event.target.files[0], "UTF-8");
    fileReader.onload = (e) => {
      var users = e.target.result.split("\n");
      var selusers = selectedUsers;
      for (var user of users) {
        if (user.length > 0 && selectedUsers.includes(user) === false) {
          selusers.push(user);
        }
      }
      // setSelectedUsers([...selectedUsers, selusers]);
      setSelectedUsers(selusers);
      forceUpdate((n) => !n);
    };
  };

  useEffect(() => {
    setShowSaveConfirmModal(false);
    setNotifyByEmail(localStorage.getItem(kNotifyByEmailStorageKey) === "true");
    if (shareStatus !== null && shareStatus !== undefined) {
      // The "Available for everyone" option (status 1) no longer has a UI
      // control, but an existing share may still carry it — treat it as
      // "selected users" so it's not left pointing at a removed option.
      setThisShareStatus(shareStatus === 1 ? 2 : shareStatus);
    } else {
      setThisShareStatus(2);
    }
    if (shareUsers !== null && shareUsers !== undefined) {
      const users = Array.isArray(shareUsers)
        ? shareUsers
        : shareUsers ? shareUsers.split(",") : [];
      setOriginalUsers(users);
      setSelectedUsers(users);
    } else {
      setSelectedUsers([]);
    }
  }, [shareStatus, shareUsers, currentTime]);

  useEffect(() => {
    if (!matchId) return;
    fetchMatchViews(matchId).then(setViewLog).catch(() => {});
  }, [matchId]);

  useEffect(() => {
    console.log(selectedUsers);
  }, [selectedUsers]);

  return (
    <>
      <div className="flex-col">
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">{t("Not Shared")}</span>
            <input
              type="radio"
              name="radio-0"
              className="radio radio-sm checked:bg-error"
              checked={thisShareStatus === 0}
              onClick={() => setThisShareStatus(0)}
            />
          </label>
        </div>
        <div className="form-control">
          <div className="flex-col">
            <label className="label cursor-pointer">
              <span className="label-text">{t("Available to selected users")}</span>
              <input
                type="radio"
                name="radio-0"
                className="radio radio-sm checked:bg-warning"
                checked={thisShareStatus === 2}
                onClick={() => setThisShareStatus(2)}
              />
            </label>
            <div
              aria-disabled={thisShareStatus !== 2}
              className={
                thisShareStatus !== 2
                  ? "flex-col mt-4 is-disabled"
                  : "flex-col mt-4"
              }
            >
              <div className="flex gap-2">
                <input
                  id="email"
                  type="text"
                  placeholder={t("Enter VBLive user email address")}
                  className="input input-sm input-bordered w-full rounded-none"
                />
                <button
                  className="btn btn-sm btn-primary rounded-none"
                  onClick={() => doAddUser()}
                >
                  {t("Add")}
                </button>
                <div>
                  <input
                    type="file"
                    id="selectUsersList"
                    ref={usersListRef}
                    style={{ display: "none" }}
                    onChange={handleUsersList}
                    onClick={(event) => {
                      event.target.value = null;
                    }}
                  />
                  <input
                    type="button"
                    className="btn btn-sm btn-primary rounded-none"
                    value={t("Import")}
                    onClick={() =>
                      document.getElementById("selectUsersList").click()
                    }
                  />
                </div>
                {/* <button className="btn btn-sm btn-primary rounded-none" onClick={() => doImportUsers()}>
                  Import
                </button> */}
              </div>
              <label className="label cursor-pointer justify-start gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={notifyByEmail}
                  onChange={(e) => changeNotifyByEmail(e.target.checked)}
                />
                <span className="label-text">
                  {t("Notify these users by email (optional)")}
                </span>
              </label>
              <div className="flex-col border mt-2 p-2">
                <div className="flex justify-between my-2">
                  <div className="text-base-content text-sm mt-2">
                    {t("Selected users:")}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-sm btn-primary rounded-none"
                      onClick={() => doSaveList()}
                    >
                      {t("Save List")}
                    </button>
                    <button
                      className="btn btn-sm btn-primary rounded-none"
                      onClick={() => doClearAllUsers()}
                    >
                      {t("Clear List")}
                    </button>
                  </div>
                </div>
                <ul className="w-full h-[20vh] border overflow-auto">
                  {selectedUsers &&
                    selectedUsers.map((user, idx) => (
                      <li
                        key={user}
                        className={
                          idx % 2 === 0
                            ? "text-base-content text-sm p-2 bg-base-200 bg-opacity-75"
                            : "text-base-content text-sm p-2"
                        }
                      >
                        <div className="flex justify-between">
                          <div>{user}</div>
                          <TrashIcon
                            className="w-5 h-5 text-base-content cursor-pointer"
                            title={t("Remove share")}
                            onClick={() => doDeleteUser(user)}
                          />
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <div className="flex gap-2">
          <button
            className="btn btn-primary btn-sm rounded-none"
            onClick={() => doShare(false)}
          >
            {t("Close")}
          </button>
          <button
            className="btn btn-primary btn-sm rounded-none"
            onClick={() => doShare(true)}
            disabled={thisShareStatus === 2 && selectedUsers.length === 0}
          >
            {t("Confirm")}
          </button>
        </div>
      </div>
      {viewLog.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <div className="text-xs font-semibold text-base-content/60 uppercase tracking-wider mb-2">View Activity</div>
          <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
            {viewLog.map((v) => (
              <li key={v.id} className="flex justify-between gap-2">
                <span className="truncate text-base-content">{v.viewerEmail}</span>
                <span className="text-base-content/50 whitespace-nowrap">
                  {v.viewedAt ? v.viewedAt.toLocaleString() : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showClearModal && (
        <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowClearModal(false); }}>
          <div className="modal-box sm:w-11/12 w-full max-w-5xl h-[24vh] rounded-none">
            <h3 className="mb-4 font-bold text-2xl">{t("Clear Users List")}</h3>
            <div className="flex flex-col">
              <div>{t("Do you really want to clear all users from list?")}</div>
              <div className="flex justify-end">
                <div className="modal-action">
                  <button className="btn btn-sm btn-info rounded-none" onClick={() => setShowClearModal(false)}>
                    {t("No")}
                  </button>
                </div>
                <div className="modal-action">
                  <button className="btn btn-sm btn-info ml-4 rounded-none" onClick={() => { setSelectedUsers([]); setShowClearModal(false); }}>
                    {t("Yes")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowClearModal(false)} />
        </dialog>
      )}

      {showSaveConfirmModal && (
        <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowSaveConfirmModal(false); }}>
          <div className="modal-box sm:w-11/12 w-full max-w-5xl h-[24vh] rounded-none">
            <h3 className="mb-4 font-bold text-2xl">{t("Share")}</h3>
            <div className="flex flex-col">
              <div>{t("Do you want to save changes before closing?")}</div>
              <div className="flex justify-end">
                <div className="modal-action">
                  <button className="btn btn-sm btn-info rounded-none" onClick={() => doConfirmSave(false)}>
                    No
                  </button>
                </div>
                <div className="modal-action">
                  <button className="btn btn-sm btn-info ml-4 rounded-none" onClick={() => doConfirmSave(true)}>
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowSaveConfirmModal(false)} />
        </dialog>
      )}

      {showFilenameModal && (
        <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowFilenameModal(false); }}>
          <div className="modal-box sm:w-11/12 w-full max-w-5xl h-[24vh] rounded-none">
            <h3 className="mb-4 font-bold text-2xl"></h3>
            <div className="flex flex-col">
              <div className="flex-col text-sm">
                <div>{t("Enter name to save list to:")}</div>
                <input
                  id="exportfilename"
                  type="text"
                  placeholder={t("Enter name to save list to")}
                  className="input input-sm input-bordered w-full rounded-none"
                />
              </div>
              <div className="flex justify-end">
                <div className="modal-action">
                  <button className="btn btn-sm btn-info rounded-none" onClick={() => setShowFilenameModal(false)}>
                    {t("Close")}
                  </button>
                </div>
                <div className="modal-action">
                  <button className="btn btn-sm btn-info ml-4 rounded-none" onClick={() => { doDoSaveList(); setShowFilenameModal(false); }}>
                    {t("Save")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowFilenameModal(false)} />
        </dialog>
      )}
    </>
  );
}

export default Share;
